import { cellKey, inBounds, isFleetLegal, randomFleet } from './placement.js';
import { pick } from './rng.js';
import { isFleetDestroyed, remainingTargets, resolveShot, shotKeys, } from './shooting.js';
/** Maksymalna liczba kolejnych przekroczeń czasu, po której gracz przegrywa walkowerem. */
export const MAX_MISSED_TURNS = 3;
export const otherSeat = (seat) => (seat === 0 ? 1 : 0);
const EMPTY_BOARD = { fleet: null, ready: false, shots: [] };
export function createGame(ruleset) {
    return {
        ruleset,
        status: 'placement',
        boards: [EMPTY_BOARD, EMPTY_BOARD],
        turn: null,
        moves: [],
        winner: null,
        endReason: null,
        missedTurns: [0, 0],
    };
}
function withBoard(state, seat, patch) {
    const boards = [state.boards[0], state.boards[1]];
    boards[seat] = { ...boards[seat], ...patch };
    return { ...state, boards };
}
function finish(state, winner, reason) {
    return {
        ok: true,
        state: { ...state, status: 'finished', turn: null, winner, endReason: reason },
        events: [{ type: 'game_over', winner, reason }],
    };
}
/**
 * Jedyne wejście do zmiany stanu partii.
 *
 * Funkcja jest czysta: przy tym samym stanie, komendzie i ziarnie RNG zawsze zwróci
 * ten sam wynik. Serwer traktuje jej wynik jako autorytatywny, klient używa jej
 * do przewidywania i walidacji.
 */
export function applyCommand(state, cmd, rng) {
    if (state.status === 'finished')
        return { ok: false, code: 'WRONG_STATUS' };
    switch (cmd.type) {
        case 'place_fleet': {
            if (state.status !== 'placement')
                return { ok: false, code: 'WRONG_STATUS' };
            if (state.boards[cmd.seat].ready)
                return { ok: false, code: 'ALREADY_READY' };
            if (!isFleetLegal(cmd.fleet, state.ruleset))
                return { ok: false, code: 'ILLEGAL_FLEET' };
            return {
                ok: true,
                state: withBoard(state, cmd.seat, { fleet: cmd.fleet }),
                events: [{ type: 'fleet_placed', seat: cmd.seat }],
            };
        }
        case 'ready': {
            if (state.status !== 'placement')
                return { ok: false, code: 'WRONG_STATUS' };
            const board = state.boards[cmd.seat];
            if (board.ready)
                return { ok: false, code: 'ALREADY_READY' };
            if (!board.fleet)
                return { ok: false, code: 'FLEET_NOT_SET' };
            const next = withBoard(state, cmd.seat, { ready: true });
            const events = [{ type: 'seat_ready', seat: cmd.seat }];
            if (!next.boards[otherSeat(cmd.seat)].ready)
                return { ok: true, state: next, events };
            return startGame(next, rng() < 0.5 ? 0 : 1, events);
        }
        case 'placement_timeout': {
            // Zabezpieczenie, a nie formalność: w partii bez limitu czasu żadna komenda
            // nie może wystartować gry za graczy, choćby harmonogram serwera się pomylił.
            if (state.ruleset.placementTimeoutSec === null)
                return { ok: false, code: 'TIMEOUTS_DISABLED' };
            if (state.status !== 'placement')
                return { ok: false, code: 'WRONG_STATUS' };
            let next = state;
            const events = [];
            for (const seat of [0, 1]) {
                if (next.boards[seat].ready)
                    continue;
                const fleet = next.boards[seat].fleet ?? randomFleet(rng, state.ruleset);
                next = withBoard(next, seat, { fleet, ready: true });
                events.push({ type: 'fleet_placed', seat }, { type: 'seat_ready', seat });
            }
            return startGame(next, rng() < 0.5 ? 0 : 1, events);
        }
        case 'fire':
            return fire(state, cmd.seat, { x: cmd.x, y: cmd.y }, false);
        case 'turn_timeout': {
            // Bez ustawionego limitu nikt nie przegrywa na czas — nawet przez błąd serwera.
            if (state.ruleset.turnTimeoutSec === null)
                return { ok: false, code: 'TIMEOUTS_DISABLED' };
            if (state.status !== 'in_progress' || state.turn === null) {
                return { ok: false, code: 'WRONG_STATUS' };
            }
            const seat = state.turn;
            const missed = state.missedTurns[seat] + 1;
            if (missed >= MAX_MISSED_TURNS) {
                return finish({ ...state, missedTurns: counterFor(state, seat, missed) }, otherSeat(seat), 'timeout');
            }
            const target = pick(rng, remainingTargets(state.boards[otherSeat(seat)].shots, state.ruleset.boardSize));
            if (!target)
                return finish(state, null, 'timeout');
            const result = fire({ ...state, missedTurns: counterFor(state, seat, missed) }, seat, target, true);
            return result;
        }
        case 'resign': {
            if (state.status !== 'in_progress')
                return { ok: false, code: 'WRONG_STATUS' };
            return finish(state, otherSeat(cmd.seat), 'resign');
        }
        case 'abandon':
            return finish(state, otherSeat(cmd.seat), 'abandoned');
        // Rezygnacja przed pierwszym strzałem (np. partia z botem, na którą gracz się rozmyślił) —
        // bez zwycięzcy i poza statystykami. Po starcie strzelania zostaje tylko poddanie się.
        case 'cancel': {
            if (state.status !== 'placement')
                return { ok: false, code: 'WRONG_STATUS' };
            return finish(state, null, 'cancelled');
        }
        default: {
            const exhaustive = cmd;
            return exhaustive;
        }
    }
}
function counterFor(state, seat, value) {
    const counters = [state.missedTurns[0], state.missedTurns[1]];
    counters[seat] = value;
    return counters;
}
function startGame(state, firstSeat, events) {
    return {
        ok: true,
        state: { ...state, status: 'in_progress', turn: firstSeat },
        events: [
            ...events,
            { type: 'game_started', firstSeat },
            { type: 'turn_changed', seat: firstSeat },
        ],
    };
}
function fire(state, seat, target, automatic) {
    if (state.status !== 'in_progress')
        return { ok: false, code: 'WRONG_STATUS' };
    if (state.turn !== seat)
        return { ok: false, code: 'NOT_YOUR_TURN' };
    const { boardSize } = state.ruleset;
    if (!inBounds(target.x, target.y, boardSize))
        return { ok: false, code: 'OUT_OF_BOUNDS' };
    const defender = otherSeat(seat);
    const board = state.boards[defender];
    if (!board.fleet)
        return { ok: false, code: 'FLEET_NOT_SET' };
    const already = shotKeys(board.shots, boardSize);
    if (already.has(cellKey(target.x, target.y, boardSize)))
        return { ok: false, code: 'ALREADY_SHOT' };
    const outcome = resolveShot(board.fleet, already, target, state.ruleset);
    const newShots = [
        ...board.shots,
        outcome.shipId === undefined
            ? { x: target.x, y: target.y, result: outcome.result }
            : { x: target.x, y: target.y, result: outcome.result, shipId: outcome.shipId },
    ];
    // Aureola zatopionego statku: pola, o których z reguł wynika, że muszą być puste.
    for (const c of outcome.revealed)
        newShots.push({ x: c.x, y: c.y, result: 'miss' });
    const seq = state.moves.length + 1;
    const move = outcome.shipId === undefined
        ? { seq, seat, x: target.x, y: target.y, result: outcome.result }
        : { seq, seat, x: target.x, y: target.y, result: outcome.result, shipId: outcome.shipId };
    let next = {
        ...withBoard(state, defender, { shots: newShots }),
        moves: [...state.moves, move],
        missedTurns: counterFor(state, seat, automatic ? state.missedTurns[seat] : 0),
    };
    const events = [
        { type: 'shot', seat, seq, x: target.x, y: target.y, outcome, automatic },
    ];
    if (isFleetDestroyed(newShots, state.ruleset)) {
        const result = finish(next, seat, 'victory');
        return result.ok
            ? { ok: true, state: result.state, events: [...events, ...result.events] }
            : result;
    }
    const keepsTurn = state.ruleset.extraShotOnHit && outcome.result !== 'miss';
    const nextSeat = keepsTurn ? seat : defender;
    next = { ...next, turn: nextSeat };
    events.push({ type: 'turn_changed', seat: nextSeat });
    return { ok: true, state: next, events };
}
/**
 * Odtworzenie partii z danych trwałych (floty + tabela `moves`).
 *
 * Dzięki temu, że każdy strzał jest zapisany, serwer po restarcie odbudowuje stan
 * bez żadnego dodatkowego mechanizmu, a widok replayu w historii dostaje kolejne
 * stany pośrednie za darmo.
 */
export function replay(ruleset, fleets, moves) {
    const fixedRng = () => 0;
    let state = createGame(ruleset);
    for (const seat of [0, 1]) {
        const placed = applyCommand(state, { type: 'place_fleet', seat, fleet: fleets[seat] }, fixedRng);
        if (!placed.ok)
            throw new Error(`replay: nielegalna flota gracza ${seat} (${placed.code})`);
        state = placed.state;
        const ready = applyCommand(state, { type: 'ready', seat }, fixedRng);
        if (!ready.ok)
            throw new Error(`replay: nie można ustawić gotowości gracza ${seat}`);
        state = ready.state;
    }
    const first = moves[0];
    if (first)
        state = { ...state, turn: first.seat };
    const timeline = [state];
    for (const move of moves) {
        const applied = applyCommand({ ...state, turn: move.seat }, { type: 'fire', seat: move.seat, x: move.x, y: move.y }, fixedRng);
        if (!applied.ok)
            throw new Error(`replay: ruch ${move.seq} odrzucony (${applied.code})`);
        state = applied.state;
        timeline.push(state);
    }
    return timeline;
}
//# sourceMappingURL=machine.js.map